/**
 * useOracleReading Hook
 * Oracle okuma akışını yöneten ana hook
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import * as oracleApi from '../services/oracleApi';

// Adımlar
export const STEPS = {
  UPLOAD: 'upload',
  INTENT: 'intent',
  DEPTH: 'depth',
  LOADING: 'loading',
  RESULT: 'result'
};

// Intent seçenekleri
export const INTENTS = [
  { id: 'general', label: 'Genel', icon: '🔮' },
  { id: 'love', label: 'Aşk', icon: '❤️' },
  { id: 'career', label: 'Kariyer', icon: '💼' },
  { id: 'money', label: 'Para', icon: '💰' },
  { id: 'family', label: 'Aile', icon: '👨‍👩‍👧' },
  { id: 'spiritual', label: 'Ruhsal', icon: '🌙' }
];

// Derinlik seçenekleri
export const DEPTHS = [
  { 
    id: 'mini', 
    name: 'Mini Açılım', 
    description: 'Kahve + 3 Tarot',
    credits: 5 
  },
  { 
    id: 'deep', 
    name: 'Derin Açılım', 
    description: 'Kahve + 7 Tarot + Şamanik',
    credits: 10 
  },
  { 
    id: 'ritual', 
    name: 'Oracle Ritüeli', 
    description: 'Kahve + 7 Tarot + 5 Katina + Şamanik',
    credits: 18 
  }
];

// Progress mesajları
const PROGRESS_STAGES = [
  { status: 'pending', icon: '⏳', message: 'Açılım hazırlanıyor...' },
  { status: 'analyzing_image', icon: '☕', message: 'Fincandaki semboller okunuyor...' },
  { status: 'drawing_cards', icon: '🃏', message: 'Kartlar çekiliyor...' },
  { status: 'generating_story', icon: '✨', message: 'Kader yolları birleştiriliyor...' },
  { status: 'formatting', icon: '📜', message: 'Açılım yazılıyor...' },
  { status: 'completed', icon: '✅', message: 'Açılım tamamlandı!' }
];

export function useOracleReading(token, userCredits, onCreditsUpdate, adminTestMode = false) {
  // Form state
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [cupImage, setCupImage] = useState(null);
  const [saucerImage, setSaucerImage] = useState(null);
  const [handleImage, setHandleImage] = useState(null);
  const [intent, setIntent] = useState(null);
  const [question, setQuestion] = useState('');
  const [depth, setDepth] = useState('deep');
  
  // Reading state
  const [readingId, setReadingId] = useState(null);
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState({ percent: 0, message: '', icon: '⏳' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Result navigation
  const [currentSection, setCurrentSection] = useState(0);
  
  // Polling ref
  const pollingRef = useRef(null);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);
  
  // Progress stage'i bul
  const getProgressStage = useCallback((statusStr) => {
    return PROGRESS_STAGES.find(s => s.status === statusStr) || PROGRESS_STAGES[0];
  }, []);
  
  // Fotoğraf yükle
  const uploadPhoto = useCallback(async (file, type) => {
    try {
      // Resize and convert to base64
      const resized = await oracleApi.resizeImage(file, 1024);
      const base64 = await oracleApi.fileToBase64(resized);
      
      if (type === 'cup') setCupImage(base64);
      else if (type === 'saucer') setSaucerImage(base64);
      else if (type === 'handle') setHandleImage(base64);
      
      return base64;
    } catch (err) {
      setError('Fotoğraf yüklenemedi');
      throw err;
    }
  }, []);
  
  // Fotoğraf sil
  const removePhoto = useCallback((type) => {
    if (type === 'cup') setCupImage(null);
    else if (type === 'saucer') setSaucerImage(null);
    else if (type === 'handle') setHandleImage(null);
  }, []);
  
  // Status polling
  const pollStatus = useCallback(async (id) => {
    try {
      const statusData = await oracleApi.getReadingStatus(token, id);
      setStatus(statusData.status);
      
      const stage = getProgressStage(statusData.status);
      setProgress({
        percent: statusData.progress_percent || 0,
        message: statusData.progress_message || stage.message,
        icon: stage.icon
      });
      
      // Tamamlandı mı?
      if (statusData.status === 'completed') {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        
        // Sonucu getir
        const resultData = await oracleApi.getReadingResult(token, id);
        setResult(resultData);
        setStep(STEPS.RESULT);
        
        // Kredi güncelle
        if (onCreditsUpdate) {
          onCreditsUpdate();
        }
      } else if (statusData.status === 'failed') {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setError(statusData.error || 'Açılım başarısız oldu');
        setStep(STEPS.UPLOAD);
      }
    } catch (err) {
      console.error('Polling error:', err);
    }
  }, [token, getProgressStage, onCreditsUpdate]);
  
  // Açılım başlat
  const startReading = useCallback(async () => {
    if (isSubmitting) return; // Çift tıklama koruması
    
    if (!cupImage) {
      setError('Lütfen fincan fotoğrafı yükleyin');
      return;
    }
    
    if (!intent) {
      setError('Lütfen niyet seçin');
      return;
    }
    
    // Admin test mode'da kredi kontrolü yapma
    const selectedDepth = DEPTHS.find(d => d.id === depth);
    if (!adminTestMode && userCredits < selectedDepth.credits) {
      setError(`Yetersiz kredi. ${selectedDepth.credits} kredi gerekli.`);
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await oracleApi.startReading(token, {
        cup_image_base64: cupImage,
        saucer_image_base64: saucerImage,
        handle_image_base64: handleImage,
        intent: intent,
        question: question || null,
        depth: depth,
        include_katina: intent === 'love' || intent === 'specific_person'
      });
      
      setReadingId(response.reading_id);
      setStatus(response.status);
      setStep(STEPS.LOADING);
      
      // Eğer hemen tamamlandıysa
      if (response.status === 'completed') {
        const resultData = await oracleApi.getReadingResult(token, response.reading_id);
        setResult(resultData);
        setStep(STEPS.RESULT);
        if (onCreditsUpdate) onCreditsUpdate();
      } else {
        // Polling başlat
        pollingRef.current = setInterval(() => {
          pollStatus(response.reading_id);
        }, 2000);
      }
    } catch (err) {
      setError(err.message);
      setStep(STEPS.DEPTH);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    cupImage, saucerImage, handleImage, intent, question, depth,
    token, userCredits, isSubmitting, pollStatus, onCreditsUpdate, adminTestMode
  ]);
  
  // Mevcut açılımı yükle (sayfa yenilendiğinde)
  const loadExistingReading = useCallback(async (id) => {
    try {
      setReadingId(id);
      const statusData = await oracleApi.getReadingStatus(token, id);
      
      if (statusData.status === 'completed') {
        const resultData = await oracleApi.getReadingResult(token, id);
        setResult(resultData);
        setStep(STEPS.RESULT);
      } else if (statusData.status === 'failed') {
        setError(statusData.error || 'Açılım başarısız oldu');
      } else {
        setStatus(statusData.status);
        setStep(STEPS.LOADING);
        
        // Polling başlat
        pollingRef.current = setInterval(() => {
          pollStatus(id);
        }, 2000);
      }
    } catch (err) {
      setError('Açılım bulunamadı');
    }
  }, [token, pollStatus]);
  
  // Takip sorusu gönder
  const sendFollowup = useCallback(async (questionText) => {
    if (!readingId) return;
    
    try {
      const response = await oracleApi.sendFollowup(token, readingId, questionText);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [token, readingId]);
  
  // Sonuç bölümü navigasyonu
  const nextSection = useCallback(() => {
    if (result && currentSection < (result.story_sections?.length || 0)) {
      setCurrentSection(prev => prev + 1);
    }
  }, [result, currentSection]);
  
  const prevSection = useCallback(() => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
    }
  }, [currentSection]);
  
  const goToSection = useCallback((index) => {
    setCurrentSection(index);
  }, []);
  
  // Reset
  const reset = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    setStep(STEPS.UPLOAD);
    setCupImage(null);
    setSaucerImage(null);
    setHandleImage(null);
    setIntent(null);
    setQuestion('');
    setDepth('deep');
    setReadingId(null);
    setStatus(null);
    setProgress({ percent: 0, message: '', icon: '⏳' });
    setResult(null);
    setError(null);
    setCurrentSection(0);
  }, []);
  
  return {
    // State
    step,
    setStep,
    cupImage,
    saucerImage,
    handleImage,
    intent,
    setIntent,
    question,
    setQuestion,
    depth,
    setDepth,
    readingId,
    status,
    progress,
    result,
    error,
    setError,
    isSubmitting,
    currentSection,
    
    // Actions
    uploadPhoto,
    removePhoto,
    startReading,
    loadExistingReading,
    sendFollowup,
    nextSection,
    prevSection,
    goToSection,
    reset,
    
    // Constants
    INTENTS,
    DEPTHS
  };
}
